/**
 * IPFS Utility Module
 *
 * Provides multi-gateway support for IPFS content with automatic fallback.
 * This improves resilience when individual IPFS gateways are slow or unavailable.
 */

// Ordered list of IPFS gateways to try (fastest/most reliable first).
//
// Every template uses the path form (/ipfs/{cid}/{path}) rather than the
// subdomain form ({cid}.ipfs.host/{path}). Subdomain gateways only accept
// base32 CIDv1, so a CIDv0 -- a "Qm..." hash, which is what this platform's
// on-chain project metadata actually stores -- makes dweb.link and w3s.link
// return HTTP 500. Path form serves both CID versions, so it is used
// throughout. Do not reintroduce a {cid}.ipfs.* template.
//
// cloudflare-ipfs.com was removed outright: Cloudflare retired that gateway
// and the hostname no longer resolves at all (NXDOMAIN), so it burned a
// failed request on every lookup.
//
// Order reflects measured round-trip latency against a known-pinned CID.
// pinata is kept last because it answers in seconds where the others answer
// in milliseconds.
export const IPFS_GATEWAYS = [
  { name: 'ipfs.io', template: 'https://ipfs.io/ipfs/{cid}/{path}' },
  { name: 'nftstorage', template: 'https://nftstorage.link/ipfs/{cid}/{path}' },
  { name: 'dweb.link', template: 'https://dweb.link/ipfs/{cid}/{path}' },
  { name: 'w3s.link', template: 'https://w3s.link/ipfs/{cid}/{path}' },
  { name: 'pinata', template: 'https://gateway.pinata.cloud/ipfs/{cid}/{path}' },
];

// Default timeout for gateway requests (ms)
const DEFAULT_TIMEOUT = 8000;

/**
 * Parse an IPFS URL into its components
 * Supports formats:
 *   - ipfs://QmCID/path
 *   - ipfs://QmCID
 *   - /ipfs/QmCID/path
 *   - https://gateway.com/ipfs/QmCID/path
 *   - https://QmCID.ipfs.gateway.com/path
 *
 * @param {string} ipfsUrl - The IPFS URL to parse
 * @returns {{ cid: string, path: string } | null} - Parsed components or null if invalid
 */
export function parseIPFSUrl(ipfsUrl) {
  if (!ipfsUrl || typeof ipfsUrl !== 'string') {
    return null;
  }

  // Format: ipfs://QmCID/path or ipfs://QmCID
  let match = ipfsUrl.match(/^ipfs:\/\/([a-zA-Z0-9]+)\/?(.*)$/);
  if (match) {
    return { cid: match[1], path: match[2] || '' };
  }

  // Format: /ipfs/QmCID/path
  match = ipfsUrl.match(/^\/ipfs\/([a-zA-Z0-9]+)\/?(.*)$/);
  if (match) {
    return { cid: match[1], path: match[2] || '' };
  }

  // Format: https://gateway.com/ipfs/QmCID/path
  match = ipfsUrl.match(/\/ipfs\/([a-zA-Z0-9]+)\/?(.*)$/);
  if (match) {
    return { cid: match[1], path: match[2] || '' };
  }

  // Format: https://QmCID.ipfs.gateway.com/path
  match = ipfsUrl.match(/^https?:\/\/([a-zA-Z0-9]+)\.ipfs\.[^/]+\/?(.*)$/);
  if (match) {
    return { cid: match[1], path: match[2] || '' };
  }

  return null;
}

/**
 * Convert an IPFS URL to a gateway URL
 *
 * @param {string} ipfsUrl - The IPFS URL (ipfs://CID/path format)
 * @param {number} gatewayIndex - Index of gateway to use (default: 0 for primary)
 * @returns {string} - Gateway URL
 */
export function ipfsToGatewayUrl(ipfsUrl, gatewayIndex = 0) {
  const parsed = parseIPFSUrl(ipfsUrl);
  if (!parsed) {
    return ipfsUrl; // Return as-is if not a valid IPFS URL
  }

  const gateway = IPFS_GATEWAYS[gatewayIndex] || IPFS_GATEWAYS[0];
  return gateway.template
    .replace('{cid}', parsed.cid)
    .replace('{path}', parsed.path);
}

/**
 * Convert a raw IPFS URL (ipfs://CID/path) to all gateway URLs
 *
 * @param {string} ipfsUrl - The IPFS URL
 * @returns {string[]} - Array of gateway URLs
 */
export function ipfsToAllGatewayUrls(ipfsUrl) {
  const parsed = parseIPFSUrl(ipfsUrl);
  if (!parsed) {
    return [ipfsUrl];
  }

  return IPFS_GATEWAYS.map(gateway =>
    gateway.template
      .replace('{cid}', parsed.cid)
      .replace('{path}', parsed.path)
  );
}

/**
 * Fetch content from IPFS with automatic gateway fallback
 *
 * @param {string} ipfsUrl - The IPFS URL to fetch
 * @param {Object} options - Options
 * @param {number} options.timeout - Timeout per gateway (ms)
 * @param {string} options.responseType - 'text', 'blob', 'json', or 'arrayBuffer'
 * @returns {Promise<any>} - Fetched content
 * @throws {Error} - If all gateways fail
 */
export async function fetchFromIPFS(ipfsUrl, options = {}) {
  const { timeout = DEFAULT_TIMEOUT, responseType = 'text' } = options;
  const gatewayUrls = ipfsToAllGatewayUrls(ipfsUrl);
  const errors = [];

  for (let i = 0; i < gatewayUrls.length; i++) {
    const url = gatewayUrls[i];
    const gatewayName = IPFS_GATEWAYS[i]?.name || `gateway-${i}`;

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      const response = await fetch(url, {
        signal: controller.signal,
        headers: {
          'Accept': '*/*',
        }
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Parse response based on type
      let data;
      switch (responseType) {
        case 'blob':
          data = await response.blob();
          break;
        case 'json':
          data = await response.json();
          break;
        case 'arrayBuffer':
          data = await response.arrayBuffer();
          break;
        case 'text':
        default:
          data = await response.text();
      }

      // Success - log and return
      console.log(`[IPFS] Fetched from ${gatewayName}: ${url}`);
      return data;

    } catch (error) {
      const errorMsg = error.name === 'AbortError'
        ? `Timeout (${timeout}ms)`
        : error.message;
      errors.push(`${gatewayName}: ${errorMsg}`);
      console.warn(`[IPFS] ${gatewayName} failed: ${errorMsg}`);
      // Continue to next gateway
    }
  }

  // All gateways failed
  const errorMessage = `All IPFS gateways failed for ${ipfsUrl}:\n${errors.join('\n')}`;
  console.error(`[IPFS] ${errorMessage}`);
  throw new Error(errorMessage);
}

/**
 * Fetch CSS from IPFS with gateway fallback and graceful failure
 *
 * @param {string} ipfsUrl - The IPFS URL for the CSS file
 * @param {number} timeout - Timeout per gateway (ms)
 * @returns {Promise<string|null>} - CSS content or null if failed
 */
export async function fetchCSSFromIPFS(ipfsUrl, timeout = 5000) {
  try {
    return await fetchFromIPFS(ipfsUrl, { timeout, responseType: 'text' });
  } catch (error) {
    console.warn('[IPFS] CSS fetch failed, continuing without custom styles');
    return null;
  }
}

/**
 * Create an image URL that tries the primary gateway
 * Returns object with primary URL and fallback URLs for error handling
 *
 * @param {string} baseUrl - IPFS base URL (e.g., ipfs://QmCID/)
 * @param {string} tokenId - Token ID
 * @param {string} extension - File extension (e.g., .png)
 * @returns {{ primary: string, fallbacks: string[] }}
 */
export function createImageUrls(baseUrl, tokenId, extension) {
  const ipfsUrl = `${baseUrl}${tokenId}${extension}`;
  const allUrls = ipfsToAllGatewayUrls(ipfsUrl);

  return {
    primary: allUrls[0],
    fallbacks: allUrls.slice(1),
    ipfsUrl: ipfsUrl
  };
}

/**
 * Check if IPFS is available by testing a gateway
 * Useful for determining if we should attempt IPFS loads at all
 *
 * @param {number} timeout - Timeout (ms)
 * @returns {Promise<{ available: boolean, gateway: string|null }>}
 */
export async function checkIPFSAvailability(timeout = 3000) {
  // Test with a known, always-pinned CID (IPFS logo)
  const testCid = 'QmYwAPJzv5CZsnA625s3Xf2nemtYgPpHdWEz79ojWnPbdG';

  for (let i = 0; i < IPFS_GATEWAYS.length; i++) {
    const gateway = IPFS_GATEWAYS[i];
    const testUrl = gateway.template
      .replace('{cid}', testCid)
      .replace('{path}', 'readme');

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      const response = await fetch(testUrl, {
        method: 'HEAD',
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        return { available: true, gateway: gateway.name, gatewayIndex: i };
      }
    } catch (error) {
      // Continue to next gateway
    }
  }

  return { available: false, gateway: null, gatewayIndex: -1 };
}

export default {
  IPFS_GATEWAYS,
  parseIPFSUrl,
  ipfsToGatewayUrl,
  ipfsToAllGatewayUrls,
  fetchFromIPFS,
  fetchCSSFromIPFS,
  createImageUrls,
  checkIPFSAvailability,
};
